/**
 * Homly — EmptyState
 * ==================
 * Composant unique pour TOUTES les pages vides.
 * Plus jamais de "Aucune donnée" froid : on rassure, on explique, on guide.
 *
 * Pattern : Illustration légère · Titre rassurant · Phrase explicative · CTA principal · CTA secondaire
 */
import React from 'react';
import { Sparkles } from 'lucide-react';

export function EmptyState({
    icon: Icon = Sparkles,
    title,
    description,
    primaryAction,        // { label, onClick, icon }
    secondaryAction,      // { label, onClick }
    illustration,         // node optionnel (image / svg custom)
    accent = 'purple',    // 'purple' | 'orange'
    className = '',
    dataTestid = 'empty-state',
}) {
    const accentColor = accent === 'orange' ? 'var(--homly-orange)' : 'var(--homly-purple)';
    const accentBg = accent === 'orange' ? 'var(--homly-orange-bg)' : 'var(--homly-purple-bg)';
    const accentBorder = accent === 'orange' ? 'var(--homly-orange-border)' : 'var(--homly-purple-border)';

    return (
        <div
            className={className}
            data-testid={dataTestid}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--homly-space-5)',
                padding: 'var(--homly-space-12) var(--homly-space-6)',
                textAlign: 'center',
                fontFamily: 'var(--homly-font-body)',
                minHeight: 360,
            }}
        >
            {/* Illustration ou icône */}
            {illustration || (
                <div
                    style={{
                        position: 'relative',
                        width: 80,
                        height: 80,
                        borderRadius: 'var(--homly-radius-xl)',
                        background: accentBg,
                        border: `1px solid ${accentBorder}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 8px 32px ${accentColor}30`,
                    }}
                >
                    {/* Glow halo */}
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            inset: -10,
                            borderRadius: 'var(--homly-radius-xl)',
                            background: `radial-gradient(circle, ${accentColor}25 0%, transparent 70%)`,
                            pointerEvents: 'none',
                        }}
                    />
                    <Icon size={32} strokeWidth={1.8} color={accentColor} aria-hidden="true" />
                </div>
            )}

            {/* Titre */}
            <h2
                style={{
                    margin: 0,
                    maxWidth: 480,
                    fontFamily: 'var(--homly-font-display)',
                    fontSize: 22,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: 'var(--homly-text-primary)',
                }}
            >
                {title}
            </h2>

            {/* Description (phrase rassurante + valeur) */}
            {description && (
                <p
                    style={{
                        margin: 0,
                        maxWidth: 520,
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: 'var(--homly-text-secondary)',
                    }}
                >
                    {description}
                </p>
            )}

            {/* CTAs */}
            {(primaryAction || secondaryAction) && (
                <div
                    style={{
                        display: 'flex',
                        gap: 'var(--homly-space-3)',
                        marginTop: 'var(--homly-space-2)',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                    }}
                >
                    {primaryAction && (
                        <button
                            type="button"
                            onClick={primaryAction.onClick}
                            data-testid={`${dataTestid}-primary-cta`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '12px 22px',
                                background: 'var(--homly-gradient-primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--homly-radius-md)',
                                fontFamily: 'var(--homly-font-display)',
                                fontSize: 14,
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: 'var(--homly-shadow-glow-purple)',
                                transition: 'transform var(--homly-transition-fast)',
                            }}
                            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                            {primaryAction.icon && <primaryAction.icon size={16} strokeWidth={2.5} />}
                            {primaryAction.label}
                        </button>
                    )}
                    {secondaryAction && (
                        <button
                            type="button"
                            onClick={secondaryAction.onClick}
                            data-testid={`${dataTestid}-secondary-cta`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '12px 22px',
                                background: 'transparent',
                                color: 'var(--homly-text-secondary)',
                                border: '1px solid var(--homly-border-strong)',
                                borderRadius: 'var(--homly-radius-md)',
                                fontFamily: 'var(--homly-font-display)',
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all var(--homly-transition-fast)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--homly-text-primary)';
                                e.currentTarget.style.borderColor = 'var(--homly-text-secondary)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--homly-text-secondary)';
                                e.currentTarget.style.borderColor = 'var(--homly-border-strong)';
                            }}
                        >
                            {secondaryAction.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
